import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ERROR_UPDATE_SHOP_DATA,
  INVALID_PHONE,
  ERROR_WRONG_INFORMATIONS,
} from '../../constants';
import {
  LegalInfo,
  ShopInfo,
  UseLaunchingFormProps,
  LaunchingFormData,
  ValidationErrors,
} from './LaunchingForm.types';
import {
  buildKeywords,
  defaultLaunchingFormServices,
  removeTypedAddress,
} from './LaunchingForm.services';
import {
  validateForm,
  validateVatNumber,
  validateEmail,
  FormattedAddress,
} from './validation.schema';

const formatAddressForDisplay = (address: FormattedAddress | null): string => {
  if (!address || !address.city) return '';
  const parts = [
    address.streetNumber,
    address.street,
    address.postalCode,
    address.city,
    address.country,
  ].filter(Boolean);
  return parts.join(', ');
};

const initialFormData: LaunchingFormData = {
  legal: {
    companyName: '',
    companyType: '',
    vatNumber: '',
    registreCommerceNumero: '',
    registreCommerceVille: '',
    capitalSocial: '',
    address: null,
    typedAddress: '',
  },
  shop: {
    name: '',
    email: '',
    number: '',
    address: null,
    typedAddress: '',
  },
  configurations: {
    businessType: '',
    businessProductType: '',
    keyWords: '',
  }
};

export function useLaunchingForm({
  user,
  showLaunchingModal,
  openErrorModal,
  services = defaultLaunchingFormServices,
}: UseLaunchingFormProps) {
  const [formData, setFormData] = useState<LaunchingFormData>(initialFormData);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isPhoneIncorrectRef = useRef<boolean>(false);

  useEffect(() => {
    if (showLaunchingModal && user) {
      const {
        legal: legalFromStore = {},
        address = null,
        shopName = '',
        number = '',
        email = '',
      } = user;

      const legalAddress = legalFromStore.address || null;
      const shopAddress = address || null;

      const mappedLegal: LegalInfo = {
        companyName: legalFromStore.companyName || shopName || '',
        companyType: legalFromStore.companyType || '',
        vatNumber: legalFromStore.vatNumber || '',
        registreCommerceNumero: legalFromStore.registreCommerceNumero || '',
        registreCommerceVille: legalFromStore.registreCommerceVille || '',
        capitalSocial: legalFromStore.capitalSocial || '',
        address: legalAddress,
        typedAddress: formatAddressForDisplay(legalAddress),
      };

      const mappedShop: ShopInfo = {
        name: shopName || '',
        email: email || '',
        number: number || '',
        address: shopAddress,
        typedAddress: formatAddressForDisplay(shopAddress),
      };

      setFormData({
        ...initialFormData,
        legal: mappedLegal,
        shop: mappedShop,
      });
      setErrors({});
    }
  }, [showLaunchingModal, user]);

  useEffect(() => {
    const currentKeyWords = formData.configurations.keyWords || '';
    const keyWordsToReturn = buildKeywords(
      currentKeyWords,
      formData.shop.name,
      formData.legal.companyName,
      formData.configurations.businessType,
      formData.shop.address,
    );

    if (keyWordsToReturn !== currentKeyWords) {
      setFormData((prev) => ({
        ...prev,
        configurations: {
          ...prev.configurations,
          keyWords: keyWordsToReturn,
        },
      }));
    }
  }, [
    formData.shop.name,
    formData.legal.companyName,
    formData.configurations.businessType,
    formData.shop.address,
  ]);

  useEffect(() => {
    if (
      formData.legal.typedAddress &&
      formData.legal.typedAddress.trim() !== '' &&
      (!formData.legal.address || !formData.legal.address.city)
    ) {
      setErrors((prev) => ({
        ...prev,
        legal: {
          ...prev.legal,
          address: 'Merci de bien sélectionner une des adresses proposées',
        },
      }));
    } else if (
      formData.legal.address &&
      formData.legal.address.city &&
      (!formData.legal.address.postalCode ||
        formData.legal.address.postalCode.trim() === '')
    ) {
      setErrors((prev) => ({
        ...prev,
        legal: {
          ...prev.legal,
          address: "Le code postal de l'adresse est requis",
        },
      }));
    } else if (
      formData.legal.address &&
      formData.legal.address.city &&
      formData.legal.address.postalCode &&
      formData.legal.address.postalCode.trim() !== ''
    ) {
      setErrors((prev) => {
        if (prev.legal?.address) {
          const { address, ...rest } = prev.legal;
          return {
            ...prev,
            legal: Object.keys(rest).length > 0 ? rest : undefined,
          };
        }
        return prev;
      });
    }
  }, [formData.legal.typedAddress, formData.legal.address]);

  useEffect(() => {
    if (
      formData.shop.typedAddress &&
      formData.shop.typedAddress.trim() !== '' &&
      (!formData.shop.address || !formData.shop.address.city)
    ) {
      setErrors((prev) => ({
        ...prev,
        shop: {
          ...prev.shop,
          address: 'Merci de bien sélectionner une des adresses proposées',
        },
      }));
    } else if (
      formData.shop.address &&
      formData.shop.address.city &&
      (!formData.shop.address.postalCode ||
        formData.shop.address.postalCode.trim() === '')
    ) {
      setErrors((prev) => ({
        ...prev,
        shop: {
          ...prev.shop,
          address: "Le code postal de l'adresse est requis",
        },
      }));
    } else if (
      formData.shop.address &&
      formData.shop.address.city &&
      formData.shop.address.postalCode &&
      formData.shop.address.postalCode.trim() !== ''
    ) {
      setErrors((prev) => {
        if (prev.shop?.address) {
          const { address, ...rest } = prev.shop;
          return {
            ...prev,
            shop: Object.keys(rest).length > 0 ? rest : undefined,
          };
        }
        return prev;
      });
    }
  }, [formData.shop.typedAddress, formData.shop.address]);

  const validateField = useCallback(
    (field: string, value: any) => {
      setErrors((prev) => {
        const newErrors = { ...prev };

        if (field === 'legal.typedAddress') {
          if (
            value &&
            value.trim() !== '' &&
            (!formData.legal.address || !formData.legal.address.city)
          ) {
            newErrors.legal = {
              ...newErrors.legal,
              address: 'Merci de bien sélectionner une des adresses proposées',
            };
          } else if (
            (!value || value.trim() === '') &&
            (!formData.legal.address || !formData.legal.address.city)
          ) {
            newErrors.legal = {
              ...newErrors.legal,
              address: 'Adresse requise',
            };
          } else if (
            formData.legal.address &&
            formData.legal.address.city &&
            (!formData.legal.address.postalCode ||
              formData.legal.address.postalCode.trim() === '')
          ) {
            newErrors.legal = {
              ...newErrors.legal,
              address: "Le code postal de l'adresse est requis",
            };
          } else if (
            formData.legal.address &&
            formData.legal.address.city &&
            formData.legal.address.postalCode &&
            formData.legal.address.postalCode.trim() !== ''
          ) {
            if (newErrors.legal?.address) {
              const { address: addressError, ...rest } = newErrors.legal;
              newErrors.legal = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'shop.typedAddress') {
          if (
            value &&
            value.trim() !== '' &&
            (!formData.shop.address || !formData.shop.address.city)
          ) {
            newErrors.shop = {
              ...newErrors.shop,
              address: 'Merci de bien sélectionner une des adresses proposées',
            };
          } else if (
            (!value || value.trim() === '') &&
            (!formData.shop.address || !formData.shop.address.city)
          ) {
            newErrors.shop = { ...newErrors.shop, address: 'Adresse requise' };
          } else if (
            formData.shop.address &&
            formData.shop.address.city &&
            (!formData.shop.address.postalCode ||
              formData.shop.address.postalCode.trim() === '')
          ) {
            newErrors.shop = {
              ...newErrors.shop,
              address: "Le code postal de l'adresse est requis",
            };
          } else if (
            formData.shop.address &&
            formData.shop.address.city &&
            formData.shop.address.postalCode &&
            formData.shop.address.postalCode.trim() !== ''
          ) {
            if (newErrors.shop?.address) {
              const { address: addressError, ...rest } = newErrors.shop;
              newErrors.shop = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'legal.companyName') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.legal = {
              ...newErrors.legal,
              companyName: "Le nom de l'entreprise est requis",
            };
          } else {
            if (newErrors.legal?.companyName) {
              const { companyName, ...rest } = newErrors.legal;
              newErrors.legal = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'legal.companyType') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.legal = {
              ...newErrors.legal,
              companyType: "Le type d'entreprise est requis",
            };
          } else {
            if (newErrors.legal?.companyType) {
              const { companyType, ...rest } = newErrors.legal;
              newErrors.legal = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'legal.vatNumber') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.legal = {
              ...newErrors.legal,
              vatNumber: 'Le numéro de TVA est requis',
            };
          } else {
            const vatError = validateVatNumber(value);
            if (vatError) {
              newErrors.legal = { ...newErrors.legal, vatNumber: vatError };
            } else {
              if (newErrors.legal?.vatNumber) {
                const { vatNumber, ...rest } = newErrors.legal;
                newErrors.legal =
                  Object.keys(rest).length > 0 ? rest : undefined;
              }
            }
          }
        } else if (field === 'legal.registreCommerceNumero') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.legal = {
              ...newErrors.legal,
              registreCommerceNumero: 'Le numéro SIREN est requis',
            };
          } else {
            if (newErrors.legal?.registreCommerceNumero) {
              const { registreCommerceNumero, ...rest } = newErrors.legal;
              newErrors.legal = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'legal.registreCommerceVille') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.legal = {
              ...newErrors.legal,
              registreCommerceVille: "La ville d'enregistrement est requise",
            };
          } else {
            if (newErrors.legal?.registreCommerceVille) {
              const { registreCommerceVille, ...rest } = newErrors.legal;
              newErrors.legal = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'shop.name') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.shop = {
              ...newErrors.shop,
              name: 'Le nom du magasin est requis',
            };
          } else {
            if (newErrors.shop?.name) {
              const { name, ...rest } = newErrors.shop;
              newErrors.shop = Object.keys(rest).length > 0 ? rest : undefined;
            }
          }
        } else if (field === 'shop.email') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.shop = { ...newErrors.shop, email: "L'email est requis" };
          } else {
            const emailError = validateEmail(value);
            if (emailError) {
              newErrors.shop = { ...newErrors.shop, email: emailError };
            } else {
              if (newErrors.shop?.email) {
                const { email, ...rest } = newErrors.shop;
                newErrors.shop =
                  Object.keys(rest).length > 0 ? rest : undefined;
              }
            }
          }
        } else if (field === 'configurations.businessType') {
          if (!value || (typeof value === 'string' && value.trim() === '')) {
            newErrors.configurations = {
              businessType: "Le type d'activité est requis",
            };
          } else {
            if (newErrors.configurations?.businessType) {
              newErrors.configurations = undefined;
            }
          }
        }

        return newErrors;
      });
    },
    [formData],
  );
  const handleFieldChange = useCallback((field: string, value: any) => {
    const fieldParts = field.split('.');

    setFormData((prev) => {
      const newData = { ...prev };
      let current: any = newData;

      for (let i = 0; i < fieldParts.length - 1; i++) {
        current = current[fieldParts[i]] = { ...current[fieldParts[i]] };
      }

      current[fieldParts[fieldParts.length - 1]] = value;

      return newData;
    });

    setErrors((prev) => {
      const newErrors = { ...prev };
      const errorParts = fieldParts.slice(0, -1);
      const errorField = fieldParts[fieldParts.length - 1];

      if (errorParts.length > 0) {
        const section = errorParts[0] as keyof ValidationErrors;
        if (newErrors[section]) {
          newErrors[section] = { ...newErrors[section] };
          delete (newErrors[section] as any)[errorField];
        }
      }

      return newErrors;
    });
  }, []);

  const handleSelectAddress = useCallback(
    async (
      addressInput: string,
      _placeId: string,
      fieldPath: 'legal.address' | 'shop.address',
      typedAddressPath: 'legal.typedAddress' | 'shop.typedAddress',
      includeLocation = false,
    ) => {
      try {
        const address = await services.geocodeAddress(
          addressInput,
          includeLocation,
        );

        if (!address) {
          return;
        }

        handleFieldChange(fieldPath, address);
        handleFieldChange(typedAddressPath, addressInput);

        setErrors((prev) => {
          const newErrors = { ...prev };
          const section = fieldPath.split('.')[0] as 'legal' | 'shop';
          if (section === 'legal' && newErrors.legal?.address) {
            const { address, ...rest } = newErrors.legal;
            newErrors.legal = Object.keys(rest).length > 0 ? rest : undefined;
          } else if (section === 'shop' && newErrors.shop?.address) {
            const { address, ...rest } = newErrors.shop;
            newErrors.shop = Object.keys(rest).length > 0 ? rest : undefined;
          }
          return newErrors;
        });
      } catch (error) {
        console.error('Error selecting address:', error);
      }
    },
    [handleFieldChange, services.geocodeAddress],
  );

  const submitFormData = useCallback(
    async (data: LaunchingFormData) => {
      if (isPhoneIncorrectRef.current) {
        openErrorModal(INVALID_PHONE);
        return;
      }

      try {
        setIsSubmitting(true);
        await services.updateShopData(
          removeTypedAddress(data.legal),
          removeTypedAddress(data.shop),
          data.configurations,
        );
      } catch (err) {
        openErrorModal(ERROR_UPDATE_SHOP_DATA);
      } finally {
        setIsSubmitting(false);
      }
    },
    [openErrorModal, services],
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();

      const validationErrors = validateForm(formData);

      const hasValidationErrors =
        (validationErrors.legal &&
          Object.keys(validationErrors.legal).length > 0) ||
        (validationErrors.shop &&
          Object.keys(validationErrors.shop).length > 0) ||
        (validationErrors.configurations &&
          Object.keys(validationErrors.configurations).length > 0);

      if (hasValidationErrors) {
        setErrors(validationErrors);
        openErrorModal(ERROR_WRONG_INFORMATIONS);
        return;
      }

      if (isPhoneIncorrectRef.current) {
        openErrorModal(INVALID_PHONE);
        return;
      }

      await submitFormData(formData);
    },
    [formData, openErrorModal, submitFormData],
  );

  const resetState = useCallback(() => {
    setFormData(initialFormData);
    setErrors({});
    isPhoneIncorrectRef.current = false;
  }, []);

  return {
    formData,
    errors,
    isSubmitting,
    isPhoneIncorrectRef,
    handleSelectAddress,
    handleSubmit,
    handleFieldChange,
    validateField,
    resetState,
  };
}

export default useLaunchingForm;
